/**
 * Zustand des Meldungsdialogs — Pendant zu den Feldern `DialogVisible`, `DialogCaption` und
 * `DialogText` der View-Seiten. Die Setzer sind einzeln gehalten, weil die Abläufe des
 * Altclients Beschriftung und Text an unterschiedlichen Stellen zuweisen.
 */

import { useCallback, useMemo, useState } from 'react';

export interface MessageDialogState {
  open: boolean;
  caption: string;
  text: string;
}

/**
 * Die Setzer sind Eigenschaften mit Funktionswert (keine Methoden), damit sie ungebunden als
 * Rückruf weitergereicht werden dürfen — wie bei `GridState`.
 */
export interface MessageDialogController extends MessageDialogState {
  /** `DialogCaption = …; DialogText = …; DialogVisible = true;` */
  show: (caption: string, text: string) => void;
  /** `DialogVisible = true; DialogText = …;` — die Beschriftung bleibt unverändert. */
  showText: (text: string) => void;
  /** `DialogText = …;` ohne Änderung der Sichtbarkeit. */
  setText: (text: string) => void;
  close: () => void;
}

const CLOSED: MessageDialogState = { open: false, caption: '', text: '' };

export function useMessageDialog(): MessageDialogController {
  const [state, setState] = useState<MessageDialogState>(CLOSED);

  const show = useCallback((caption: string, text: string) => {
    setState({ open: true, caption, text });
  }, []);

  const showText = useCallback((text: string) => {
    setState((current) => ({ ...current, open: true, text }));
  }, []);

  const setText = useCallback((text: string) => {
    setState((current) => ({ ...current, text }));
  }, []);

  const close = useCallback(() => {
    setState((current) => ({ ...current, open: false }));
  }, []);

  return useMemo(
    () => ({ ...state, show, showText, setText, close }),
    [close, setText, show, showText, state],
  );
}

export default useMessageDialog;
